import MarkdownPreview from "@uiw/react-markdown-preview";
import "./markdown-styles.css";

import { FC, memo, useEffect, useRef, useState } from "react";

type HastNode = {
  type?: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

type HastElement = HastNode & {
  type: "element";
  tagName: string;
  properties: Record<string, unknown>;
};

const isHastElement = (
  node: HastNode | null | undefined
): node is HastElement => {
  return Boolean(
    node && node.type === "element" && typeof node.tagName === "string"
  );
};

const stripFootnoteBackrefs = (node: HastNode | null | undefined) => {
  if (!node || !Array.isArray(node.children)) {
    return;
  }

  node.children = node.children.filter((child) => {
    if (
      isHastElement(child) &&
      child.properties &&
      ("data-footnote-backref" in child.properties ||
        "dataFootnoteBackref" in child.properties)
    ) {
      return false;
    }

    stripFootnoteBackrefs(child);
    return true;
  });
};

const stripFootnoteArtifacts = (node: HastNode | null | undefined) => {
  if (!isHastElement(node) || !node.properties) {
    return;
  }

  const hasFootnotesDataAttr =
    "data-footnotes" in node.properties || "dataFootnotes" in node.properties;

  if (!hasFootnotesDataAttr) {
    return;
  }

  if (Array.isArray(node.children)) {
    node.children = node.children.filter((child) => {
      if (isHastElement(child)) {
        const headingId = child.properties?.["id"];
        const isFootnoteHeading =
          child.tagName === "h2" &&
          typeof headingId === "string" &&
          headingId === "footnote-label";

        if (isFootnoteHeading) {
          return false;
        }
      }

      stripFootnoteBackrefs(child);
      return true;
    });
  }
};

const MarkdownTextImpl: FC<{
  children: string;
  collapseReferences?: boolean;
}> = ({ children, collapseReferences = false }) => {
  const [referencesOpen, setReferencesOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    const handleToggle = (event: Event) => {
      if (
        event.target instanceof HTMLDetailsElement &&
        event.target.hasAttribute("data-reference-details")
      ) {
        setReferencesOpen(event.target.open);
      }
    };
    container?.addEventListener("toggle", handleToggle, true);
    return () => container?.removeEventListener("toggle", handleToggle, true);
  }, []);
  return (
    <div
      ref={containerRef}
      className="markdown-content overflow-x-auto divide-gray-200 border border-gray-200"
      onClick={(event) => {
        if (!collapseReferences || !(event.target instanceof Element)) return;
        const citation = event.target.closest<HTMLAnchorElement>(
          "a[data-footnote-ref]"
        );
        const href = citation?.getAttribute("href");
        if (!href?.startsWith("#")) return;
        const target = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            "[data-footnotes] [id]"
          )
        ).find((element) => element.id === href.slice(1));
        const details = target?.closest<HTMLDetailsElement>(
          "details[data-reference-details]"
        );
        if (!target || !details) return;
        event.preventDefault();
        details.open = true;
        setReferencesOpen(true);
        target.scrollIntoView({ block: "nearest" });
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }}
    >
      <MarkdownPreview
        source={children}
        wrapperElement={{ "data-color-mode": "light" }}
        className="text-base bg-transparent"
        // Disable links in headings
        rehypeRewrite={(node, _, parent) => {
          if (isHastElement(node) && parent && isHastElement(parent)) {
            if (node.tagName === "a" && /^h[1-6]$/.test(parent.tagName)) {
              if (Array.isArray(parent.children)) {
                parent.children = parent.children.slice(1);
              }
            }
          }

          const hastNode: HastNode = node;
          stripFootnoteArtifacts(hastNode);
          if (
            collapseReferences &&
            isHastElement(hastNode) &&
            ("data-footnotes" in hastNode.properties ||
              "dataFootnotes" in hastNode.properties)
          ) {
            const list = hastNode.children?.find(
              (child) => isHastElement(child) && child.tagName === "ol"
            );
            const count =
              list?.children?.filter(
                (child) => isHastElement(child) && child.tagName === "li"
              ).length ?? 0;
            if (count > 0) {
              hastNode.children = [
                {
                  type: "element",
                  tagName: "details",
                  properties: {
                    "data-reference-details": "",
                    open: referencesOpen,
                  },
                  children: [
                    {
                      type: "element",
                      tagName: "summary",
                      properties: {},
                      children: [
                        { type: "text", value: `References (${count})` },
                      ],
                    },
                    ...(hastNode.children ?? []),
                  ],
                },
              ];
            }
          }
        }}
      />
    </div>
  );
};

export const MarkdownText = memo(MarkdownTextImpl);
